import { EntityType, EntityConfig, EntityGroup } from '@/types/customizing';
import { PrivilegeEnum, CollaboratorDto, ApiKeyDto } from '@/api/ApiClient';

// Type-safe wrapper for DataGrid valueGetter
export const createTypedValueGetter = <T>(getter: (entity: T) => any) => {
  return (_rawValue: any, row: T) => {
    return getter(row);
  };
};

export const ENTITY_CONFIGS: Record<EntityType, EntityConfig & { privilege: PrivilegeEnum }> = {
  users: {
    title: 'Users',
    privilege: PrivilegeEnum.User,
    alternatePrivileges: [PrivilegeEnum.ResearchGroup],
    // Customizing list APIs require List; Staff tab in KO also shows on Read but load needs List in practice.
    visibilityOperations: ['read', 'list'],
    gridAccessOperation: 'list',
    hiddenColumns: ['id', 'phone'],
    columns: [
      { field: 'id', headerName: 'ID', width: 80, description: 'Internal database identifier' },
      { field: 'firstName', headerName: 'First Name', width: 130, description: "User's first name" },
      { field: 'lastName', headerName: 'Last Name', width: 130, description: "User's last name" },
      { field: 'abbreviation', headerName: 'Abbr', width: 80, description: 'Short abbreviation for the user' },
      { field: 'orcId', headerName: 'ORCID', width: 150, description: 'Open Researcher and Contributor ID' },
      {
        field: 'role',
        headerName: 'Role',
        width: 120,
        description: 'Primary role assigned to the user',
        valueGetter: createTypedValueGetter<CollaboratorDto>((entity) => entity?.roles?.[0]?.name || ''),
      },
      {
        field: 'facultyName',
        headerName: 'Faculty',
        width: 120,
        description: 'Faculty member supervising this user',
      },
      {
        field: 'eppns',
        headerName: 'EPPNs',
        width: 160,
        description: 'Sign-in identifiers (Shibboleth / Azure)',
        valueGetter: createTypedValueGetter<CollaboratorDto>((entity) => {
          const rows = entity?.eppns;
          if (rows?.length) {
            if (rows.length === 1) {
              return rows[0].eppn ?? '';
            }
            return `${rows.length} EPPNs`;
          }
          return '';
        }),
      },
      {
        field: 'techniques',
        headerName: 'Synthesis Techniques',
        width: 200,
        description: 'Synthesis techniques this user is trained on',
      },
      {
        field: 'managedInstruments',
        headerName: 'Managed Instruments',
        width: 200,
        description: 'Instruments this user manages',
      },
      { field: 'position', headerName: 'Position', width: 150, description: 'Job title or position' },
      {
        field: 'institution',
        headerName: 'Institution',
        width: 200,
        description: 'Affiliated institution or organization',
      },
      { field: 'city', headerName: 'City', width: 120 },
      { field: 'state', headerName: 'State', width: 80 },
      { field: 'zip', headerName: 'Zip', width: 80 },
      { field: 'country', headerName: 'Country', width: 80 },

      {
        field: 'isAdmin',
        headerName: 'Admin',
        width: 80,
        description: 'Can switch to admin role',
        valueGetter: createTypedValueGetter<CollaboratorDto>((entity) => (entity?.isAdmin ? '✓' : '')),
      },
      {
        field: 'inactive',
        headerName: 'Inactive',
        width: 100,
        description: 'User account is inactive - cannot log in',
        valueGetter: createTypedValueGetter<CollaboratorDto>((entity) => (entity?.inactive ? '✓' : '')),
      },
      { field: 'email', headerName: 'Email', width: 200 },
      { field: 'phone', headerName: 'Phone', width: 120 },
      { field: 'userType', headerName: 'Type', width: 80 },
    ],
    fields: [
      { name: 'firstName', label: 'First Name', type: 'text', required: true },
      { name: 'lastName', label: 'Last Name', type: 'text', required: true },
      { name: 'abbreviation', label: 'Abbreviation', type: 'text' },
      { name: 'orcId', label: 'ORCID', type: 'text' },
      { name: 'faculty', label: 'Faculty', type: 'autocomplete', options: 'facultyOptions' },
      { name: 'roleIds', label: 'Role', type: 'select', required: true, options: 'userRoles' },
      {
        name: 'techniques',
        label: 'Synthesis Techniques',
        type: 'multiselect-autocomplete',
        options: 'synthesisTechniques',
      },
      {
        name: 'managedInstruments',
        label: 'Managed Instruments',
        type: 'multiselect-autocomplete',
        options: 'synthesisInstruments',
      },
      { name: 'position', label: 'Position', type: 'text' },
      { name: 'institution', label: 'Institution', type: 'text' },
      { name: 'city', label: 'City', type: 'text' },
      { name: 'state', label: 'State', type: 'text' },
      { name: 'zip', label: 'Zip', type: 'text' },
      { name: 'country', label: 'Country', type: 'text' },
      { name: 'email', label: 'Email', type: 'email' },
      { name: 'phone', label: 'Phone', type: 'text' },
      { name: 'isAdmin', label: 'Admin', type: 'boolean' },
      { name: 'inactive', label: 'Inactive', type: 'boolean' },
    ],
  },
  apiKeys: {
    title: 'API Keys',
    privilege: PrivilegeEnum.ApiKeys,
    visibilityOperations: ['create', 'read', 'list'],
    columns: [
      { field: 'name', headerName: 'Name', width: 150 },
      {
        field: 'keyType',
        headerName: 'Type',
        width: 120,
        valueGetter: createTypedValueGetter<ApiKeyDto>((entity) => (entity?.userId ? 'User-based' : 'Role-based')),
      },
      {
        field: 'role',
        headerName: 'Role',
        width: 150,
        valueGetter: createTypedValueGetter<ApiKeyDto>((entity) => entity?.role?.name || ''),
      },
      {
        field: 'userName',
        headerName: 'User',
        width: 220,
        valueGetter: createTypedValueGetter<ApiKeyDto>((entity) => entity?.userName || entity?.userId || ''),
      },
      {
        field: 'created',
        headerName: 'Created',
        width: 150,
        type: 'date',
        valueGetter: createTypedValueGetter<ApiKeyDto>((entity) => (entity?.created ? new Date(entity.created) : '')),
      },
      {
        field: 'validTo',
        headerName: 'Expires',
        width: 150,
        type: 'date',
        valueGetter: createTypedValueGetter<ApiKeyDto>((entity) => new Date(entity?.validTo)),
      },
      {
        field: 'allowInternalApi',
        headerName: 'Allow Internal API',
        width: 150,
        valueGetter: createTypedValueGetter<ApiKeyDto>((entity) => (entity?.allowInternalApi ? '✓' : '')),
      },
      {
        field: 'projects',
        headerName: 'Projects',
        width: 150,
        valueGetter: createTypedValueGetter<ApiKeyDto>(
          (entity) => entity?.projects?.map((p) => p.projectLabel).join(', ') || ''
        ),
      },
      {
        field: 'createdByUserName',
        headerName: 'Created By',
        width: 220,
        valueGetter: createTypedValueGetter<ApiKeyDto>(
          (entity) => entity?.createdByUserName || entity?.createdByUserId || ''
        ),
      },
      {
        field: 'email',
        headerName: 'Email',
        width: 200,
        valueGetter: createTypedValueGetter<ApiKeyDto>((entity) => entity?.email || ''),
      },
      {
        field: 'institution',
        headerName: 'Institution',
        width: 200,
        valueGetter: createTypedValueGetter<ApiKeyDto>((entity) => entity?.institution || ''),
      },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'roleId', label: 'Role', type: 'select', required: true, options: 'roles' },
      { name: 'created', label: 'Created', type: 'datetime-local', disabled: true },
      { name: 'validTo', label: 'Valid To', type: 'datetime-local', required: true },
      { name: 'allowInternalApi', label: 'Allow Internal API', type: 'boolean' },
      { name: 'projects', label: 'Projects', type: 'project-access' },
    ],
  },
  roles: {
    title: 'Roles',
    privilege: PrivilegeEnum.Role,
    columns: [
      { field: 'name', headerName: 'Name', width: 150 },
      {
        field: 'isUser',
        headerName: 'User Role',
        description: 'Can be assigned to users',
        width: 100,
        valueGetter: createTypedValueGetter<any>((entity) => (entity?.isUser ? '✓' : '')),
      },
      {
        field: 'isFaculty',
        headerName: 'Faculty',
        description: 'Defines faculty members - owns instruments, leads research groups',
        width: 100,
        valueGetter: createTypedValueGetter<any>((entity) => (entity?.isFaculty ? '✓' : '')),
      },
      {
        field: 'isResearcher',
        headerName: 'Researcher',
        description: 'Defines researchers - working with instruments, belongs to a research group of a faculty member',
        width: 120,
        valueGetter: createTypedValueGetter<any>((entity) => (entity?.isResearcher ? '✓' : '')),
      },
      {
        field: 'isInhouse',
        headerName: 'In-house',
        description: 'Defines in-house users - They have at least read access to all data',
        width: 100,
        valueGetter: createTypedValueGetter<any>((entity) => (entity?.isInhouse ? '✓' : '')),
      },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'isUser', label: 'User Role', type: 'boolean' },
      { name: 'isFaculty', label: 'Faculty', type: 'boolean' },
      { name: 'isResearcher', label: 'Researcher', type: 'boolean' },
      { name: 'isInhouse', label: 'In-house', type: 'boolean' },
    ],
  },
  synthesisTechniques: {
    title: 'Synthesis Techniques',
    privilege: PrivilegeEnum.SynthesisTechnique,
    columns: [
      { field: 'id', headerName: 'ID', width: 100 },
      { field: 'name', headerName: 'Name', width: 150 },
      { field: 'parentTechnique', headerName: 'Synthesis Group', width: 150 },
      { field: 'instrumentTypes', headerName: 'Instrument Types', width: 200 },
      {
        field: 'growOnSubstrate',
        headerName: 'Grow on Substrate',
        width: 150,
        valueGetter: createTypedValueGetter<any>((entity) => (entity?.growOnSubstrate ? '✓' : '')),
      },
    ],
    fields: [
      { name: 'id', label: 'ID', type: 'text', required: true },
      { name: 'name', label: 'Name', type: 'text', required: true },
      {
        name: 'instrumentTypes',
        label: 'Instrument Types',
        type: 'multiselect-autocomplete',
        options: 'synthesisInstrumentTypes',
        required: true,
      },
      { name: 'parentTechnique', label: 'Synthesis Group', type: 'autocomplete', options: 'synthesisTechniques' },
      { name: 'growOnSubstrate', label: 'Grow on Substrate', type: 'boolean' },
    ],
  },
  synthesisInstrumentTypes: {
    title: 'Synthesis Instrument Types',
    privilege: PrivilegeEnum.SynthesisTechnique,
    columns: [
      { field: 'id', headerName: 'ID', width: 100 },
      { field: 'label', headerName: 'Label', width: 200 },
      { field: 'synthesisTechniques', headerName: 'Synthesis Techniques', width: 200 },
    ],
    fields: [
      { name: 'id', label: 'ID', type: 'text', required: true },
      { name: 'label', label: 'Label', type: 'text', required: true },
      {
        name: 'synthesisTechniques',
        label: 'Synthesis Techniques',
        type: 'multiselect-autocomplete',
        options: 'synthesisTechniques',
      },
    ],
  },
  synthesisInstruments: {
    title: 'Synthesis Instruments',
    privilege: PrivilegeEnum.SynthesisInstrument,
    hiddenColumns: ['description'],
    columns: [
      { field: 'id', headerName: 'ID', width: 100 },
      { field: 'name', headerName: 'Name', width: 150 },
      { field: 'doi', headerName: 'DOI', width: 150 },
      {
        field: 'faculty',
        headerName: 'Faculty',
        width: 120,
        valueGetter: createTypedValueGetter<any>((entity) => {
          // This will be resolved by the EntityDataService
          return entity.facultyDisplayName || entity.faculty || '';
        }),
      },
      { field: 'location', headerName: 'Location', width: 120 },
      { field: 'instrumentType', headerName: 'Instrument Type', width: 150 },
    ],
    fields: [
      { name: 'id', label: 'ID', type: 'text', required: true },
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'doi', label: 'DOI', type: 'text' },
      { name: 'location', label: 'Location', type: 'text' },
      {
        name: 'instrumentType',
        label: 'Instrument Type',
        type: 'select',
        options: 'synthesisInstrumentTypes',
        required: true,
      },
      { name: 'faculty', label: 'Faculty', type: 'select', options: 'facultyOptions', required: true },
      { name: 'description', label: 'Description', type: 'richtext' },
      { name: 'metadataConfig', label: 'File Metadata', type: 'instrument-file-metadata' },
    ],
  },
  simulationMethods: {
    title: 'Simulation Methods',
    privilege: PrivilegeEnum.SimulationMethod,
    columns: [
      { field: 'id', headerName: 'ID', width: 80 },
      { field: 'name', headerName: 'Name', width: 150 },
      {
        field: 'managers',
        headerName: 'Managers',
        width: 200,
        valueGetter: createTypedValueGetter<any>((entity) => {
          // This will be resolved by the EntityDataService
          return entity.managersDisplayName || entity.managers || '';
        }),
      },
    ],
    fields: [
      { name: 'id', label: 'ID', type: 'text', required: true },
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'managers', label: 'Managers', type: 'multiselect-autocomplete', options: 'researcherFacultyOptions' },
    ],
  },
  characterizationTechniques: {
    title: 'Characterization Techniques',
    privilege: PrivilegeEnum.CharacterizationTechnique,
    columns: [
      { field: 'id', headerName: 'ID', width: 100 },
      { field: 'name', headerName: 'Name', width: 200 },
    ],
    fields: [
      { name: 'id', label: 'ID', type: 'text', required: true },
      { name: 'name', label: 'Name', type: 'text', required: true },
    ],
  },
  characterizationInstruments: {
    title: 'Characterization Instruments',
    privilege: PrivilegeEnum.CharacterizationInstrument,
    hiddenColumns: ['description'],
    columns: [
      { field: 'id', headerName: 'ID', width: 100 },
      { field: 'name', headerName: 'Name', width: 150 },
      { field: 'doi', headerName: 'DOI', width: 150 },
      { field: 'facility', headerName: 'Facility', width: 120 },
      { field: 'location', headerName: 'Location', width: 120 },
      { field: 'characterizationTechnique', headerName: 'Technique', width: 150 },
    ],
    fields: [
      { name: 'id', label: 'ID', type: 'text', required: true },
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'doi', label: 'DOI', type: 'text' },
      { name: 'facility', label: 'Facility', type: 'text' },
      { name: 'location', label: 'Location', type: 'text' },
      {
        name: 'characterizationTechnique',
        label: 'Characterization Technique',
        type: 'select',
        options: 'characterizationTechniques',
      },
      { name: 'description', label: 'Description', type: 'richtext' },
      { name: 'metadataConfig', label: 'File Metadata', type: 'instrument-file-metadata' },
    ],
  },
  materials: {
    title: 'Materials',
    privilege: PrivilegeEnum.Material,
    hiddenColumns: ['externalId'],
    hideDeleteWhenUsed: true,
    columns: [
      { field: 'name', headerName: 'Name', width: 150 },
      { field: 'label', headerName: 'Label', width: 150 },
      { field: 'externalId', headerName: 'External ID', width: 120 },
      {
        field: 'isConfidential',
        headerName: 'Confidential',
        width: 100,
        valueGetter: createTypedValueGetter<any>((entity) => (entity?.isConfidential ? '✓' : '')),
      },
      {
        field: 'isSubstrate',
        headerName: 'Substrate only',
        width: 120,
        valueGetter: createTypedValueGetter<any>((entity) => (entity?.isSubstrate ? '✓' : '')),
      },
      {
        field: 'isUsed',
        headerName: 'Used',
        width: 80,
        valueGetter: createTypedValueGetter<any>((entity) => (entity?.isUsed ? '✓' : '')),
      },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'label', label: 'Label', type: 'text' },
      { name: 'externalId', label: 'External ID', type: 'text' },
      { name: 'isConfidential', label: 'Confidential', type: 'boolean' },
      { name: 'isSubstrate', label: 'Substrate', type: 'boolean' },
    ],
  },
  rawMaterials: {
    title: 'Bulk Raw Materials',
    privilege: PrivilegeEnum.BulkRawMaterial,
    columns: [
      { field: 'name', headerName: 'Name', width: 150 },
      { field: 'vendor', headerName: 'Vendor', width: 120 },
      { field: 'lot', headerName: 'Lot', width: 120 },
      { field: 'purity', headerName: 'Purity', width: 100 },
      { field: 'granularity', headerName: 'Granularity', width: 120 },
      {
        field: 'materialID',
        headerName: 'Material',
        width: 150,
        valueGetter: createTypedValueGetter<any>((entity) => {
          // This will be resolved by the EntityDataService to show material label
          return entity.materialLabel || entity.materialDisplayName || entity.materialID || '';
        }),
      },
      {
        field: 'techniques',
        headerName: 'Synthesis Techniques',
        width: 200,
        description: 'Synthesis techniques associated with this bulk raw material',
        valueGetter: createTypedValueGetter<any>((entity) => {
          // Use the display version if available, otherwise fall back to joining the array
          return (
            entity.techniquesDisplay ||
            (Array.isArray(entity.techniques) ? entity.techniques.join(', ') : entity.techniques || '')
          );
        }),
      },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'vendor', label: 'Vendor', type: 'text' },
      { name: 'lot', label: 'Lot', type: 'text' },
      { name: 'purity', label: 'Purity', type: 'number' },
      { name: 'granularity', label: 'Granularity', type: 'text' },
      { name: 'materialID', label: 'Material', type: 'autocomplete', options: 'materials' },
      {
        name: 'techniques',
        label: 'Synthesis Techniques',
        type: 'multiselect-autocomplete',
        options: 'synthesisTechniques',
      },
    ],
  },
  bulkElements: {
    title: 'Molar Weights',
    privilege: PrivilegeEnum.Material,
    columns: [
      { field: 'name', headerName: 'Name', width: 150 },
      { field: 'molarMass', headerName: 'Molar Mass', width: 120 },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'molarMass', label: 'Molar Mass', type: 'number' },
    ],
  },
  reportingCategories: {
    title: 'Reporting Categories',
    privilege: PrivilegeEnum.ReportingCategory,
    visibilityOperations: ['create', 'read', 'update', 'delete'] as const, // Show if user has ANY
    allowEditWhenUsed: true,
    hideDeleteWhenUsed: true,
    columns: [
      { field: 'id', headerName: 'ID', width: 80, description: 'Internal database identifier' },
      { field: 'code', headerName: 'Code', width: 150 },
      { field: 'label', headerName: 'Label', width: 200 },
      {
        field: 'requiresInternalSamplePrivilege',
        headerName: 'Requires Internal Privilege',
        width: 200,
        valueGetter: createTypedValueGetter<any>((entity) => (entity?.requiresInternalSamplePrivilege ? '✓' : '')),
      },
      {
        field: 'isUsed',
        headerName: 'Is Used',
        width: 100,
        valueGetter: createTypedValueGetter<any>((entity) => {
          if (entity?.isUsed === undefined || entity?.isUsed === null) return '';
          return entity.isUsed ? '✓' : '';
        }),
      },
    ],
    fields: [
      { name: 'code', label: 'Code', type: 'text', required: true },
      { name: 'label', label: 'Label', type: 'text', required: true },
      { name: 'requiresInternalSamplePrivilege', label: 'Requires Internal Sample Privilege', type: 'boolean' },
    ],
  },
  projectTypes: {
    title: 'Project Types',
    privilege: PrivilegeEnum.ProjectType,
    allowEditWhenUsed: true,
    hideDeleteWhenUsed: true,
    columns: [
      { field: 'id', headerName: 'ID', width: 80, description: 'Internal database identifier' },
      { field: 'code', headerName: 'Code', width: 150 },
      { field: 'label', headerName: 'Label', width: 200 },
      {
        field: 'isInternal',
        headerName: 'Internal',
        width: 100,
        valueGetter: createTypedValueGetter<any>((entity) => (entity?.isInternal ? '✓' : '')),
      },
      { field: 'prefix', headerName: 'Prefix', width: 100 },
      {
        field: 'isUsed',
        headerName: 'Is Used',
        width: 100,
        valueGetter: createTypedValueGetter<any>((entity) => {
          if (entity?.isUsed === undefined || entity?.isUsed === null) return '';
          return entity.isUsed ? '✓' : '';
        }),
      },
    ],
    fields: [
      { name: 'code', label: 'Code', type: 'text', required: true },
      { name: 'label', label: 'Label', type: 'text', required: true },
      { name: 'isInternal', label: 'Internal', type: 'boolean', required: true },
      { name: 'prefix', label: 'Prefix', type: 'text', required: true },
    ],
  },
  instrumentFunding: {
    title: 'Instrument Funding',
    privilege: PrivilegeEnum.InstrumentFunding,
    visibilityOperations: ['create', 'delete'],
    hideDeleteWhenUsed: true,
    columns: [
      { field: 'id', headerName: 'ID', width: 80, description: 'Internal database identifier' },
      { field: 'code', headerName: 'Code', width: 150 },
      { field: 'label', headerName: 'Label', width: 200 },
      {
        field: 'isUsed',
        headerName: 'Is Used',
        width: 100,
        valueGetter: createTypedValueGetter<any>((entity) => {
          if (entity?.isUsed === undefined || entity?.isUsed === null) return '';
          return entity.isUsed ? '✓' : '';
        }),
      },
    ],
    fields: [
      { name: 'code', label: 'Code', type: 'text', required: true },
      { name: 'label', label: 'Label', type: 'text', required: true },
    ],
  },
};

export const ENTITY_GROUPS: EntityGroup[] = [
  {
    title: 'Authorization',
    entities: [
      { key: 'apiKeys', config: ENTITY_CONFIGS.apiKeys },
      { key: 'roles', config: ENTITY_CONFIGS.roles },
    ],
  },
  {
    title: 'Users',
    entities: [{ key: 'users', config: ENTITY_CONFIGS.users }],
  },
  {
    title: 'Synthesis',
    entities: [
      { key: 'synthesisTechniques', config: ENTITY_CONFIGS.synthesisTechniques },
      { key: 'synthesisInstrumentTypes', config: ENTITY_CONFIGS.synthesisInstrumentTypes },
      { key: 'synthesisInstruments', config: ENTITY_CONFIGS.synthesisInstruments },
      { key: 'instrumentFunding', config: ENTITY_CONFIGS.instrumentFunding },
    ],
  },
  {
    title: 'Simulation',
    entities: [{ key: 'simulationMethods', config: ENTITY_CONFIGS.simulationMethods }],
  },
  {
    title: 'Characterization',
    entities: [
      { key: 'characterizationTechniques', config: ENTITY_CONFIGS.characterizationTechniques },
      { key: 'characterizationInstruments', config: ENTITY_CONFIGS.characterizationInstruments },
    ],
  },
  {
    title: 'Materials',
    entities: [
      { key: 'materials', config: ENTITY_CONFIGS.materials },
      { key: 'rawMaterials', config: ENTITY_CONFIGS.rawMaterials },
      { key: 'bulkElements', config: ENTITY_CONFIGS.bulkElements },
    ],
  },
  {
    title: 'Reporting',
    entities: [
      { key: 'reportingCategories', config: ENTITY_CONFIGS.reportingCategories },
      { key: 'projectTypes', config: ENTITY_CONFIGS.projectTypes },
    ],
  },
];
